import React, {useEffect, useState} from "react";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {Checkbox} from "primereact/checkbox";
import Icon from "../../../system/components/icon";
import {MILESTONE_RESULTS, PROJECT_STATES, TASK_ACTIONS} from "../../../models/project-update/project-update-api";
import "../../goals/components/goal-updates.css";
import "./project-updates.css";
import {RichTextEditor} from "../../../system/components/rich-text";

// Closing workflow (spec §11): warning and outcome details → open tasks → milestones → linked goals → confirm.
// Nothing changes until «انتشار به‌روزرسانی و بستن پروژه»; the backend closes the project in one transaction.

const STEPS = [
    {key: "outcome", title: "نتیجه پروژه"},
    {key: "tasks", title: "کارهای باز"},
    {key: "milestones", title: "مایلستون‌ها"},
    {key: "goals", title: "اهداف متصل"},
    {key: "confirm", title: "تأیید نهایی"},
];
const STEP_OF_ERROR = {"closure.result": 0, "closure.evidence": 0, "closure.reason": 0, "closure.decisionMaker": 0, "closure.lastState": 0, "closure.lessons": 0, "closure.tasks": 1, "closure.milestones": 2, "closure.goals": 3};

function Field({label, error, required, children}) {
    return (
        <label className="flex flex-column gap-2">
            <span className="text-sm font-bold">{label}{required && <span style={{color: "#e6531e"}}> *</span>}</span>
            {children}
            {error && <span className="text-xs" style={{color: "#dc2626"}}>{error}</span>}
        </label>
    );
}

function ProjectUpdateClosure({visible, state, closure, onChange, context, memberships, errors: allErrors = {}, publishing, onPublish, onSaveDraft, onHide}) {
    const errors = Object.fromEntries(Object.entries(allErrors).filter(([key]) => key.startsWith("closure.")));
    const [step, setStep] = useState(0);
    const config = PROJECT_STATES[state];
    const openTasks = context?.openTasks || [];
    const openMilestones = context?.openMilestones || [];
    const goals = context?.linkedGoals || [];
    const otherProjects = (context?.otherProjects || []).map((project) => ({label: project.title, value: project.documentId}));
    const people = (memberships || []).map((membership) => ({label: membership?.member?.fullname || membership?.member?.username || "—", value: membership.documentId}));

    // jump to the first step with a server error
    useEffect(() => {
        const steps = Object.keys(allErrors).map((key) => STEP_OF_ERROR[key]).filter((value) => value !== undefined);
        if (steps.length) setStep(Math.min(...steps));
    }, [allErrors]);
    useEffect(() => {
        if (visible) setStep(0);
    }, [visible]);

    const set = (patch) => onChange({...closure, ...patch});
    const taskDecision = (documentId) => closure.tasks.find((item) => item.task === documentId) || {task: documentId, action: null, targetProject: null};
    const setTask = (documentId, patch) => set({tasks: [...closure.tasks.filter((item) => item.task !== documentId), {...taskDecision(documentId), ...patch}]});
    const setAllTasks = (action) => set({tasks: openTasks.map((task) => ({...taskDecision(task.documentId), action}))});
    const milestoneDecision = (documentId) => closure.milestones.find((item) => item.milestone === documentId) || {milestone: documentId, result: null, note: ""};
    const setMilestone = (documentId, patch) => set({milestones: [...closure.milestones.filter((item) => item.milestone !== documentId), {...milestoneDecision(documentId), ...patch}]});
    const goalDecision = (documentId) => closure.goals.find((item) => item.goal === documentId) || {goal: documentId, contribution: state === "achieved" ? 100 : 0, stillValid: true, needsReplacement: false, requestReview: state !== "achieved", note: ""};
    const setGoal = (documentId, patch) => set({goals: [...closure.goals.filter((item) => item.goal !== documentId), {...goalDecision(documentId), ...patch}]});

    // decisions the user has not touched still count with their defaults
    useEffect(() => {
        if (!visible) return;
        const missingGoals = goals.filter((goal) => !closure.goals.some((item) => item.goal === goal.documentId));
        if (missingGoals.length) set({goals: [...closure.goals, ...missingGoals.map((goal) => goalDecision(goal.documentId))]});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, goals.length]);

    const decidedTasks = openTasks.filter((task) => taskDecision(task.documentId).action).length;
    const decidedMilestones = openMilestones.filter((milestone) => milestoneDecision(milestone.documentId).result).length;

    const body = () => {
        if (step === 0) {
            return (
                <div className="flex flex-column gap-4">
                    <div className="gu-banner text-sm" style={{lineHeight: 1.9}}>
                        <span className="flex gap-2"><span className="inline-flex flex-none" style={{color: "#c2410c"}}><Icon icon="mi mi:info" className="text-xl"/></span>
                            با انتشار این به‌روزرسانی، پروژه بسته می‌شود و از فهرست پروژه‌های فعال خارج خواهد شد. کارها، مایلستون‌ها و اثر پروژه بر اهداف متصل باید تعیین تکلیف شوند.</span>
                    </div>
                    {state === "achieved" && (
                        <>
                            <Field label="دستاورد نهایی پروژه" required error={errors["closure.result"]}>
                                <RichTextEditor value={closure.result} onChange={(html) => set({result: html})} placeholder="خروجی اصلی‌ای که این پروژه تحویل داد…" minHeight="5rem"/>
                            </Field>
                            <Field label="شواهد یا توضیح تحقق" required error={errors["closure.evidence"]}>
                                <RichTextEditor value={closure.evidence} onChange={(html) => set({evidence: html})} placeholder="لینک، گزارش یا توضیحی که تحقق خروجی را نشان می‌دهد…" minHeight="5rem"/>
                            </Field>
                            <Field label="درس‌آموخته‌ها (اختیاری)">
                                <RichTextEditor value={closure.lessons} onChange={(html) => set({lessons: html})} minHeight="5rem"/>
                            </Field>
                        </>
                    )}
                    {state === "canceled" && (
                        <>
                            <Field label="دلیل لغو" required error={errors["closure.reason"]}>
                                <RichTextEditor value={closure.reason} onChange={(html) => set({reason: html})} placeholder="تغییر اولویت، بودجه، راهبرد یا شرایط بیرونی…" minHeight="5rem"/>
                            </Field>
                            <div className="grid m-0">
                                <div className="col-12 md:col-6 p-0 md:pl-2">
                                    <Field label="تصمیم‌گیرنده" required error={errors["closure.decisionMaker"]}>
                                        <Dropdown value={closure.decisionMaker} options={people} filter placeholder="انتخاب عضو" className="w-full text-sm" onChange={(event) => set({decisionMaker: event.value})}/>
                                    </Field>
                                </div>
                                <div className="col-12 md:col-6 p-0 md:pr-2 mt-3 md:mt-0">
                                    <Field label="پروژه جایگزین (اگر هست)">
                                        <Dropdown value={closure.replacementProject} options={otherProjects} filter showClear placeholder="بدون پروژه جایگزین" className="w-full text-sm" onChange={(event) => set({replacementProject: event.value})}/>
                                    </Field>
                                </div>
                            </div>
                        </>
                    )}
                    {state === "archived" && (
                        <>
                            <Field label="دلیل رها شدن" required error={errors["closure.reason"]}>
                                <RichTextEditor value={closure.reason} onChange={(html) => set({reason: html})} minHeight="5rem"/>
                            </Field>
                            <Field label="آخرین وضعیت واقعی پروژه و میزان خروجی تحقق‌یافته" required error={errors["closure.lastState"]}>
                                <RichTextEditor value={closure.lastState} onChange={(html) => set({lastState: html})} minHeight="5rem"/>
                            </Field>
                            <Field label="درس‌آموخته‌ها و پیشنهاد جلوگیری از تکرار" required error={errors["closure.lessons"]}>
                                <RichTextEditor value={closure.lessons} onChange={(html) => set({lessons: html})} minHeight="5rem"/>
                            </Field>
                        </>
                    )}
                </div>
            );
        }
        if (step === 1) {
            return (
                <div className="flex flex-column gap-3">
                    <p className="m-0 text-sm text-600" style={{lineHeight: 1.9}}>کارهای باز خودکار «انجام شده» نمی‌شوند؛ برای هر کار تصمیم بگیرید.</p>
                    {errors["closure.tasks"] && <span className="text-xs" style={{color: "#dc2626"}}>{errors["closure.tasks"]}</span>}
                    {!openTasks.length ? <div className="gu-card p-3 text-sm text-600">کار بازی باقی نمانده است.</div> : (
                        <>
                            <div className="flex flex-wrap align-items-center gap-2 text-xs">
                                <span className="text-600">برای همه:</span>
                                {TASK_ACTIONS.filter((action) => action.value !== "move_to_project").map((action) => (
                                    <button key={action.value} type="button" className="pu-chip-button" onClick={() => setAllTasks(action.value)}>{action.label}</button>
                                ))}
                            </div>
                            {openTasks.map((task) => {
                                const decision = taskDecision(task.documentId);
                                return (
                                    <div key={task.documentId} className="gu-card p-3 flex flex-column md:flex-row md:align-items-center gap-2">
                                        <div className="flex-1 min-w-0">
                                            <p className="m-0 text-sm font-bold">{task.title}</p>
                                            <p className="m-0 text-2xs text-500">{[task.assignee, task.taskStatus === "blocked" ? "مسدود" : task.taskStatus === "doing" ? "در حال انجام" : "برای انجام"].filter(Boolean).join(" · ")}</p>
                                        </div>
                                        <Dropdown value={decision.action} options={TASK_ACTIONS} placeholder="تصمیم برای این کار" className="text-xs" style={{minWidth: "14rem"}} onChange={(event) => setTask(task.documentId, {action: event.value})}/>
                                        {decision.action === "move_to_project" && (
                                            <Dropdown value={decision.targetProject} options={otherProjects} filter placeholder="پروژه مقصد" className="text-xs" style={{minWidth: "12rem"}} onChange={(event) => setTask(task.documentId, {targetProject: event.value})}/>
                                        )}
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            );
        }
        if (step === 2) {
            return (
                <div className="flex flex-column gap-3">
                    {errors["closure.milestones"] && <span className="text-xs" style={{color: "#dc2626"}}>{errors["closure.milestones"]}</span>}
                    {!openMilestones.length ? <div className="gu-card p-3 text-sm text-600">مایلستون بازی وجود ندارد.</div> : openMilestones.map((milestone) => {
                        const decision = milestoneDecision(milestone.documentId);
                        return (
                            <div key={milestone.documentId} className="gu-card p-3 flex flex-column gap-2">
                                <div className="flex flex-column md:flex-row md:align-items-center gap-2">
                                    <p className="m-0 flex-1 text-sm font-bold">{milestone.title}</p>
                                    <Dropdown value={decision.result} options={MILESTONE_RESULTS} placeholder="وضعیت نهایی" className="text-xs" style={{minWidth: "12rem"}} onChange={(event) => setMilestone(milestone.documentId, {result: event.value})}/>
                                </div>
                                <input className="pu-input" value={decision.note} placeholder="توضیح (اختیاری)" onChange={(event) => setMilestone(milestone.documentId, {note: event.target.value})}/>
                            </div>
                        );
                    })}
                </div>
            );
        }
        if (step === 3) {
            return (
                <div className="flex flex-column gap-3">
                    <p className="m-0 text-sm text-600" style={{lineHeight: 1.9}}>بستن پروژه هدف را خودکار نمی‌بندد. برای هر هدف متصل اثر این پروژه را مشخص کنید؛ اگر بازبینی بخواهید، مسئول هدف اعلان می‌گیرد.</p>
                    {errors["closure.goals"] && <span className="text-xs" style={{color: "#dc2626"}}>{errors["closure.goals"]}</span>}
                    {!goals.length ? <div className="gu-card p-3 text-sm text-600">این پروژه به هدفی متصل نیست.</div> : goals.map((goal) => {
                        const decision = goalDecision(goal.documentId);
                        return (
                            <div key={goal.documentId} className="gu-card p-3 flex flex-column gap-3">
                                <div className="flex align-items-center justify-content-between gap-2">
                                    <div>
                                        <p className="m-0 text-sm font-bold">{goal.title}</p>
                                        {goal.owner?.member?.fullname && <p className="m-0 text-2xs text-500">مسئول هدف: {goal.owner.member.fullname}</p>}
                                    </div>
                                    <span className="flex align-items-center gap-2 text-xs">
                                        سهم تحقق‌یافته
                                        <InputNumber value={decision.contribution} min={0} max={100} suffix="٪" inputClassName="text-center text-xs" inputStyle={{width: "4.5rem"}} onValueChange={(event) => setGoal(goal.documentId, {contribution: event.value ?? 0})}/>
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-4 text-xs">
                                    {[
                                        {key: "stillValid", label: "هدف همچنان معتبر است"},
                                        {key: "needsReplacement", label: "پروژه جایگزین لازم است"},
                                        {key: "requestReview", label: "مسئول هدف آن را بازبینی کند"},
                                    ].map((option) => (
                                        <label key={option.key} className="flex align-items-center gap-2 cursor-pointer">
                                            <Checkbox checked={Boolean(decision[option.key])} onChange={(event) => setGoal(goal.documentId, {[option.key]: event.checked})}/>
                                            {option.label}
                                        </label>
                                    ))}
                                </div>
                                <input className="pu-input" value={decision.note} placeholder="توضیح (اختیاری)" onChange={(event) => setGoal(goal.documentId, {note: event.target.value})}/>
                            </div>
                        );
                    })}
                </div>
            );
        }
        return (
            <div className="flex flex-column gap-3 text-sm" style={{lineHeight: 1.9}}>
                <div className="gu-card p-3 flex flex-column gap-2">
                    <span>نتیجه: <strong>{config?.label}</strong></span>
                    <span>کارهای باز تعیین‌تکلیف‌شده: <strong>{decidedTasks.toLocaleString("fa-IR")} از {openTasks.length.toLocaleString("fa-IR")}</strong></span>
                    <span>مایلستون‌ها: <strong>{decidedMilestones.toLocaleString("fa-IR")} از {openMilestones.length.toLocaleString("fa-IR")}</strong></span>
                    <span>اهداف متصل: <strong>{goals.length.toLocaleString("fa-IR")}</strong>{closure.goals.some((item) => item.requestReview) ? " · درخواست بازبینی برای مسئول هدف ارسال می‌شود" : ""}</span>
                </div>
                <p className="m-0 text-600">پس از انتشار، پروژه با این نتیجه بسته می‌شود و از فهرست پروژه‌های فعال خارج می‌شود؛ سابقه آن حذف نمی‌شود.</p>
                {Object.keys(errors).length > 0 && <span className="text-xs" style={{color: "#dc2626"}}>چند مورد کامل نشده است؛ مراحل مشخص‌شده را بررسی کنید.</span>}
            </div>
        );
    };

    const footer = (
        <div className="gu flex align-items-center justify-content-between gap-2">
            <button type="button" className="gu-soft-button gu-soft-button--plain" onClick={onSaveDraft} disabled={publishing}>ذخیره به‌عنوان پیش‌نویس</button>
            <div className="flex gap-2">
                {step > 0 && <button type="button" className="gu-soft-button gu-soft-button--gray" onClick={() => setStep(step - 1)}>قبلی</button>}
                {step < STEPS.length - 1
                    ? <button type="button" className="gu-soft-button gu-soft-button--solid" onClick={() => setStep(step + 1)}>بعدی</button>
                    : <button type="button" className="gu-soft-button gu-soft-button--solid" disabled={publishing} onClick={onPublish}>
                        <Icon icon="mi mi:lock" className="text-lg"/>{publishing ? "در حال انتشار…" : "انتشار به‌روزرسانی و بستن پروژه"}
                    </button>}
            </div>
        </div>
    );

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={<span className="text-lg font-bold">بستن پروژه · {config?.label}</span>}
            footer={footer}
            className="gu-dialog rtl"
            style={{width: "min(54rem, 96vw)"}}
            draggable={false}
            blockScroll
        >
            <div className="gu shabnam-font flex flex-column gap-4" dir="rtl">
                <div className="pu-steps">
                    {STEPS.map((item, index) => (
                        <button key={item.key} type="button" className={`pu-step ${index === step ? "is-active" : index < step ? "is-done" : ""}`} onClick={() => setStep(index)}>
                            <span className="pu-step__number">{(index + 1).toLocaleString("fa-IR")}</span>{item.title}
                        </button>
                    ))}
                </div>
                {body()}
            </div>
        </Dialog>
    );
}

export default ProjectUpdateClosure;
